import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Ban,
  Check,
  CircleDollarSign,
  CirclePause,
  Hand,
  type LucideIcon,
  Minus,
  MoreHorizontal,
  RotateCcw,
  Square,
  Truck,
  X,
} from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Skeleton } from '~/components/ui/skeleton';
import { formatUsd } from '~/features/cost-estimate/utils/format-usd';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';

import type { NeedsYouGate } from '../hooks/use-needs-you-rows';
import { useRunCreatorLabel } from '../hooks/use-run-creators';
import { useRunTitle } from '../hooks/use-run-title';
import { getRunProgress } from '../services/run.service';
import type { RunRead } from '../types/run.api.types';
import { isMovingStatus, isRetryableStatus } from '../utils/batches';
import { BATCHES_TABLE_TEST_ID, isRecentRun } from '../utils/batches-table';
import { catalogLabel } from '../utils/catalog-labels';
import { AUDIT_FLOW } from '../utils/feed-audit';
import { limitRunRowRead } from '../utils/limiter';
import { formatCount } from '../utils/run-format';
import { isRunStopped } from '../utils/run-status';

import { BatchStepTrackBar } from './batch-status';

/** The icon each status wears in the Status column (moving ones get a pulsing dot instead). */
const STATUS_ICON: Record<string, LucideIcon> = {
  AWAITING_GATE: Hand,
  COST_GATE: CircleDollarSign,
  FAILED: X,
  REJECTED: Ban,
  COMPLETED: Check,
  DELIVERING: Truck,
  PENDING: CirclePause,
  TERMINATED: Square,
  RESET: RotateCcw,
  SKIPPED: Minus,
};

/**
 * The Status badge's fill: waiting amber, live blue, completed green, a stop in the last week
 * red. Old stops and the rest are neutral, so red always means "look at this now".
 */
function statusTone(status: string, recent: boolean): string {
  if (status === 'AWAITING_GATE' || status === 'COST_GATE') return 'bg-warning-50 text-warning';
  if (isMovingStatus(status)) return 'bg-info-50 text-info';
  if (recent && status === 'FAILED') return 'bg-error-50 text-error';
  if (recent && status === 'REJECTED') return 'bg-danger-50 text-danger';
  if (status === 'COMPLETED') return 'bg-success-50 text-success';
  return 'bg-neutral-20 text-secondary-text';
}

/**
 * The CE status badge (guide §4.8), shared by the Batches table and the batch header: icon + word
 * ("Waiting on you", "Running · 62%", "Failed"); red/amber only when it matters now.
 */
export function StatusCell({
  run,
  status,
  testId,
  plain = false,
}: {
  run: RunRead;
  status: string;
  /** Defaults to the table's per-row id; the batch header passes its own. */
  testId?: string;
  /** No pill: icon + label in the status colour (the batch header's status band). */
  plain?: boolean;
}) {
  // From the item counts, not `percent` (whose scale the contract doesn't pin down).
  const items = run.progress;
  const percent =
    items && items.itemsTotal > 0
      ? Math.min(100, Math.round((items.itemsDone / items.itemsTotal) * 100))
      : 0;
  return (
    <RunStatusBadge
      status={status}
      recent={isRecentRun(run)}
      percent={percent}
      testId={testId ?? `${BATCHES_TABLE_TEST_ID}-status-${run.id}`}
      plain={plain}
    />
  );
}

/**
 * The same badge from a bare status — for lists that hold a batch's status and date but not its
 * run record (a product's Batches table). `recent` applies the 7-day rule to red.
 */
export function RunStatusBadge({
  status,
  recent,
  percent = 0,
  testId,
  plain = false,
}: {
  status: string;
  recent: boolean;
  percent?: number;
  testId?: string;
  /** Icon + label in the status colour with no pill — a line that already sits on a tinted band. */
  plain?: boolean;
}) {
  const { t } = useTranslation('catalog-enrichment-runs');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const Icon = STATUS_ICON[status];
  const label =
    status === 'AWAITING_GATE'
      ? t('batchesTable.status.waiting')
      : status === 'COST_GATE'
        ? t('batchesTable.status.cost')
        : status === 'RUNNING' && percent > 0
          ? t('batchesTable.status.runningPercent', { percent })
          : catalogLabel(tCatalog, 'runStatus', status);
  return (
    <span
      data-testid={testId}
      className={cn(
        plain
          ? 'inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap'
          : 'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        plain
          ? statusTone(status, recent)
              .split(' ')
              .filter((c) => /(^|:)text-/.test(c))
              .join(' ')
          : statusTone(status, recent),
      )}
    >
      {Icon ? (
        <Icon aria-hidden="true" className="size-3.5 shrink-0" />
      ) : (
        <span aria-hidden="true" className="flex size-3.5 shrink-0 items-center justify-center">
          <span className="size-1.5 rounded-full bg-current motion-safe:animate-pulse" />
        </span>
      )}
      {label}
    </span>
  );
}

/** The batch's name, and underneath who (or what) started it. */
export function BatchCell({ run }: { run: RunRead }) {
  const { t } = useTranslation('catalog-enrichment-runs');
  const { title } = useRunTitle(run, undefined);
  const creatorLabel = useRunCreatorLabel();
  const creator = run.audit.createdBy;
  const startedBy =
    run.flow === AUDIT_FLOW
      ? t('batchesTable.by.audit')
      : run.triggerSource === 'SCHEDULE'
        ? run.scheduleName
          ? t('batchesTable.by.schedule', { name: run.scheduleName })
          : t('batchesTable.by.scheduler')
        : run.experimentId
          ? t('batchesTable.by.experiment', {
              name: run.experimentName ?? t('table.experimentChip'),
            })
          : creator?.actorId
            ? creatorLabel({
                actorId: creator.actorId,
                handle: creator.handle ?? null,
                kind: creator.kind,
              })
            : null;
  return (
    <div className="flex min-w-0 flex-col">
      <span className="flex min-w-0 items-center gap-2">
        <span className="text-foreground truncate font-medium" title={title}>
          {title}
        </span>
        {run.archivedAt && (
          <span className="text-secondary-text shrink-0 text-xs">{t('table.archived')}</span>
        )}
      </span>
      {startedBy && <span className="text-secondary-text truncate text-xs">{startedBy}</span>}
    </div>
  );
}

/** The step track, and the step it is on (or stopped at) in the batch page's own words. */
export function StageCell({ run, status }: { run: RunRead; status: string }) {
  const { t } = useTranslation('catalog-enrichment-runs');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const label =
    status === 'COMPLETED'
      ? t('batchesTable.stage.done')
      : run.currentStage
        ? catalogLabel(tCatalog, 'stage', run.currentStage)
        : null;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <BatchStepTrackBar progress={run.stageProgress} status={status} recent={isRecentRun(run)} />
      {label && (
        <span
          className={cn(
            'truncate text-xs',
            status === 'COMPLETED' ? 'text-success' : 'text-secondary-text',
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}

/** Keeps a click or key inside the actions cell from also opening the row. */
function stop(e: MouseEvent | KeyboardEvent) {
  e.stopPropagation();
}

export interface ActionHandlers {
  statusOf: (run: RunRead) => string;
  retryingIds: ReadonlySet<string>;
  /** The archived view restores instead of archiving. */
  archiveMode: 'archive' | 'unarchive';
  onRetry: (run: RunRead) => void;
  onPeek: (run: RunRead) => void;
  onArchive: (run: RunRead, mode: 'archive' | 'unarchive') => void;
  /** Before following a waiting batch's Review link (remembers the queue for "Next"). */
  onReview: (run: RunRead) => void;
  /** What a waiting batch waits for (gate stage, cost), when it waits at all. */
  gateOf: (run: RunRead) => NeedsYouGate | undefined;
}

/** Stages whose gate is a look at generated content. */
const CONTENT_GATES = new Set(['SAMPLE', 'ENRICHMENT', 'POST_ENRICH_SCORE']);

/**
 * The waiting row's one button says exactly what is asked (guide §5.1): "Approve $41.20" for a
 * cost gate the user can decide, "Approve publish", "Review content", else "Review". Null when
 * someone else has to decide.
 */
function useReviewLabel() {
  const { t } = useTranslation('catalog-enrichment-runs');
  return (gate: NeedsYouGate | undefined): string | null => {
    if (gate?.kind === 'cost') {
      if (!gate.canDecide) return null;
      return gate.costUsd !== null
        ? t('batchesTable.actions.approveCost', { amount: formatUsd(gate.costUsd) })
        : t('batchesTable.actions.approveCostNoAmount');
    }
    if (gate?.stage === 'APPROVAL') return t('batchesTable.actions.approvePublish');
    if (gate?.stage && CONTENT_GATES.has(gate.stage))
      return t('batchesTable.actions.reviewContent');
    return t('batchesTable.actions.review');
  };
}

/**
 * One contextual button — Review for a waiting batch, Retry for a stop in the last week — then a
 * ⋯ menu with Quick look, Retry (older stops), Copy id and Archive.
 */
export function ActionsCell({ run, handlers }: { run: RunRead; handlers: ActionHandlers }) {
  const { t } = useTranslation('catalog-enrichment-runs');
  const { title } = useRunTitle(run, undefined);
  const status = handlers.statusOf(run);
  // An archived batch is put away: no retry from the list (restore it first).
  const canRetry = isRetryableStatus(status) && !!run.currentStage && !run.archivedAt;
  const retryInline = canRetry && isRecentRun(run);
  const id = `${BATCHES_TABLE_TEST_ID}-${run.id}`;
  const reviewLabel = useReviewLabel()(handlers.gateOf(run));
  const waiting = status === 'AWAITING_GATE' || status === 'COST_GATE';

  return (
    // Not a control itself: it only stops clicks / keys on its buttons from opening the row.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="flex items-center justify-end gap-1" onClick={stop} onKeyDown={stop}>
      {waiting && reviewLabel && (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="h-8 cursor-pointer"
          data-testid={`${id}-review-button`}
        >
          <Link
            to="/catalog-enrichment/runs/$runId/gate"
            params={{ runId: run.id }}
            onClick={() => {
              handlers.onReview(run);
            }}
          >
            {reviewLabel}
          </Link>
        </Button>
      )}
      {retryInline && (
        <Button
          size="sm"
          variant="outline"
          className="h-8 cursor-pointer"
          isLoading={handlers.retryingIds.has(run.id)}
          data-testid={`${id}-retry-button`}
          onClick={() => {
            handlers.onRetry(run);
          }}
        >
          {t('list.retry')}
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="text-secondary-text hover:text-foreground size-8 cursor-pointer"
            aria-label={t('table.rowActions.menuLabel', { name: title })}
            data-testid={`${id}-menu-button`}
          >
            <MoreHorizontal aria-hidden="true" className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid={`${id}-menu`}>
          <DropdownMenuItem
            className="cursor-pointer"
            data-testid={`${id}-menu-peek`}
            onSelect={() => {
              handlers.onPeek(run);
            }}
          >
            {t('batchesTable.actions.peek')}
          </DropdownMenuItem>
          {canRetry && !retryInline && (
            <DropdownMenuItem
              className="cursor-pointer"
              data-testid={`${id}-menu-retry`}
              onSelect={() => {
                handlers.onRetry(run);
              }}
            >
              {t('list.retry')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="cursor-pointer"
            data-testid={`${id}-menu-copy-id`}
            onSelect={() => {
              copyToClipboard(run.id);
              toast.success(t('table.rowActions.copied'));
            }}
          >
            {t('batchesTable.actions.copyId')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            data-testid={`${id}-menu-archive`}
            onSelect={() => {
              handlers.onArchive(run, handlers.archiveMode);
            }}
          >
            {handlers.archiveMode === 'archive'
              ? t('table.rowActions.archive')
              : t('table.rowActions.unarchive')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * The batch's product count — the same number its header shows (`/progress`
 * `totals.productsCurated`, the distinct catalog products), never the ledger's
 * `productsSucceeded`. Read per visible row through the shared run-row limiter and cached with
 * the batch page; listings on hover. "—" before Matching has counted products.
 */
export function ProductsCell({ run }: { run: RunRead }) {
  const { t } = useTranslation('catalog-enrichment-runs');
  const progress = useQuery({
    queryKey: queryKeys.catalogService.runProgress(run.id),
    queryFn: () => limitRunRowRead(() => getRunProgress(run.id)),
    staleTime: isRunStopped(run.status) || run.status === 'COMPLETED' ? Infinity : 30_000,
  });
  if (progress.isPending) return <Skeleton className="ml-auto h-4 w-8" />;
  const products = progress.data?.totals.productsCurated ?? 0;
  const listings = progress.data?.totals.itemsTotal ?? run.progress?.itemsTotal ?? 0;
  const reached = products > 0 || run.status === 'COMPLETED' || isRunStopped(run.status);
  return (
    <span
      className="text-foreground tabular-nums"
      data-testid={`${BATCHES_TABLE_TEST_ID}-products-${run.id}`}
      title={listings > 0 ? t('batchesTable.productsTitle', { count: listings }) : undefined}
    >
      {reached ? formatCount(products) : '—'}
    </span>
  );
}
